import * as React from 'react';

import { cn } from '../lib/cn.js';

interface TabsContextValue {
  value: string;
  setValue: (next: string) => void;
  baseId: string;
  registerTrigger: (value: string, ref: HTMLButtonElement | null) => void;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error('Tabs.* must be rendered inside <Tabs>');
  return ctx;
}

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ value: valueProp, defaultValue, onValueChange, className, children, id, ...props }, ref) => {
    const [internal, setInternal] = React.useState<string>(defaultValue ?? '');
    const isControlled = valueProp !== undefined;
    const value = isControlled ? (valueProp ?? '') : internal;

    const reactId = React.useId();
    const baseId = id ?? `tabs-${reactId.replace(/:/g, '')}`;

    const triggersRef = React.useRef(new Map<string, HTMLButtonElement | null>());
    const registerTrigger = React.useCallback((v: string, node: HTMLButtonElement | null) => {
      if (node === null) triggersRef.current.delete(v);
      else triggersRef.current.set(v, node);
    }, []);

    const setValue = React.useCallback(
      (next: string) => {
        if (!isControlled) setInternal(next);
        onValueChange?.(next);
      },
      [isControlled, onValueChange],
    );

    const contextValue = React.useMemo<TabsContextValue>(
      () => ({ value, setValue, baseId, registerTrigger }),
      [value, setValue, baseId, registerTrigger],
    );

    return (
      <TabsContext.Provider value={contextValue}>
        <div ref={ref} id={baseId} className={cn('flex flex-col gap-4', className)} {...props}>
          {children}
        </div>
      </TabsContext.Provider>
    );
  },
);
Tabs.displayName = 'Tabs';

const TabsList = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="tablist"
      className={cn('inline-flex items-center gap-2', className)}
      {...props}
    />
  ),
);
TabsList.displayName = 'TabsList';

interface TabsTriggerProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  value: string;
}

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ className, value, onKeyDown, children, ...props }, forwardedRef) => {
    const { value: activeValue, setValue, baseId, registerTrigger } = useTabsContext();
    const isActive = activeValue === value;
    const innerRef = React.useRef<HTMLButtonElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLButtonElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
        registerTrigger(value, node);
      },
      [forwardedRef, registerTrigger, value],
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight' &&
        event.key !== 'Home' &&
        event.key !== 'End'
      ) {
        return;
      }
      const list = innerRef.current?.parentElement;
      if (!list) return;
      const triggers = Array.from(
        list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'),
      );
      const currentIndex = triggers.indexOf(innerRef.current as HTMLButtonElement);
      if (currentIndex < 0) return;
      let nextIndex = currentIndex;
      if (event.key === 'ArrowLeft')
        nextIndex = (currentIndex - 1 + triggers.length) % triggers.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % triggers.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = triggers.length - 1;
      event.preventDefault();
      triggers[nextIndex]?.focus();
      triggers[nextIndex]?.click();
    };

    return (
      <button
        ref={setRefs}
        type="button"
        role="tab"
        id={`${baseId}-trigger-${value}`}
        aria-selected={isActive}
        aria-controls={`${baseId}-panel-${value}`}
        tabIndex={isActive ? 0 : -1}
        data-state={isActive ? 'active' : 'inactive'}
        onClick={(event) => {
          props.onClick?.(event);
          setValue(value);
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          'inline-flex h-9 items-center gap-2 rounded-sm px-4 font-sans text-[13px]',
          'transition-colors duration-200 ease-jq',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jq-action-accent/60 focus-visible:ring-offset-2 focus-visible:ring-offset-jq-bg-base',
          isActive
            ? 'bg-jq-action-primary font-semibold text-jq-action-primary-fg shadow-sm'
            : 'font-medium text-jq-fg-2 hover:text-jq-fg-1',
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);
TabsTrigger.displayName = 'TabsTrigger';

interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ className, value, ...props }, ref) => {
    const { value: activeValue, baseId } = useTabsContext();
    const isActive = activeValue === value;
    if (!isActive) return null;
    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`${baseId}-panel-${value}`}
        aria-labelledby={`${baseId}-trigger-${value}`}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: WAI-ARIA recommends tabpanels be focusable so keyboard users can scroll the panel content
        tabIndex={0}
        className={cn('focus-visible:outline-none', className)}
        {...props}
      />
    );
  },
);
TabsContent.displayName = 'TabsContent';

export { Tabs, TabsContent, TabsList, TabsTrigger };
